import { useRouter } from 'next/router';
import Breadcrumb from '~/components/Breadcrumb';
import Container from '~/components/Container';
import ClientReviewItem from '~/components/Items/ClientReviewItem';
import Pagination from '~/components/Pagination';
import SectionTitle from '~/components/SectionTitle';
import Select from '~/components/Select';
import Text from '~/components/Text';

import { useMemo, useState } from 'react';
import ModalCarousel from '~/components/ImagesReviewCarousel/ModalCarousel';
import { ReviewItem } from '~/data/product/interfaces';

type Props = {
    data: {
        page_info?: {
            page_size?: number;
            current_page?: number;
            total_pages?: number;
        };
        items?: ReviewItem[];
    };
};

const Reviews = ({ data }: Props) => {
    const router = useRouter();
    const query = router?.query || {};
    const [initialSlide, setInitialSlide] = useState(0);

    const [open, setOpen] = useState(false);

    const paging = data?.page_info ?? {};
    const reviews = data?.items ?? ([] as ReviewItem[]);

    const totalPage = Number(paging?.total_pages ?? 1);
    const page = Number(paging?.current_page ?? 1);
    const pageSize = Number(paging?.page_size ?? 12);
    const sort = (query?.sort as string) ?? 'rating-DESC';

    const handleOnPageChange = (newPage: number) => {
        router.push(
            {
                query: {
                    ...query,
                    currentPage: newPage?.toString()
                }
            },
            undefined
        );
    };

    const onChangeFilter = (key: any, value: any) => {
        return router.push(
            {
                pathname: router.pathname,
                query: {
                    ...router.query,
                    [key]: value,
                    currentPage: 1
                }
            },
            undefined
        );
    };

    const images: string[] = useMemo(() => {
        if (!reviews?.length) return [];
        return (
            reviews
                .map((item) => item?.images)
                ?.filter((i) => !!i)
                .flat() || []
        );
    }, [reviews]);

    return (
        <Container rootClassName="py-10 lg:py-16" className="flex flex-col gap-10 lg:gap-16">
            <Breadcrumb items={[{ title: 'Home', path: '/' }, { title: 'Reviews' }]} />

            <SectionTitle
                text="Reviews"
                action={
                    <div className="flex mb-2 justify-between w-full gap-6 items-center lg:basis-1/2">
                        <div className="flex items-center flex-1 gap-5">
                            <Text type="label-3" htmlTag="label" className="shrink-0 hidden lg:block">
                                Sort by
                            </Text>
                            <Select
                                className="flex-1"
                                options={[
                                    { label: 'Highest Rating', value: 'rating-DESC' },
                                    { label: 'Most recent', value: 'date-DESC' },
                                    { label: 'Lowest Rating', value: 'rating-ASC' }
                                ]}
                                value={sort}
                                onChange={(value: string) => onChangeFilter('sort', value)}
                            />
                        </div>
                        <div className="flex items-center flex-1 gap-5">
                            <Text type="label-3" htmlTag="label" className="hidden lg:block">
                                Show
                            </Text>
                            <Select
                                className="flex-1"
                                options={[
                                    { label: '12', value: '12' },
                                    { label: '24', value: '24' },
                                    { label: '36', value: '36' }
                                ]}
                                value={pageSize.toString()}
                                onChange={(value: string) => onChangeFilter('pageSize', value)}
                            />
                        </div>
                    </div>
                }
            />

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4 mason-grid">
                {reviews?.map((item, index) => (
                    <ClientReviewItem
                        onClickImage={() => {
                            setInitialSlide(index);
                            setOpen(true);
                        }}
                        data={item}
                        key={index}
                        className="mason-grid-item"
                    />
                ))}
            </div>
            <Pagination
                totalPage={totalPage}
                currentPage={page}
                pageSize={pageSize}
                onPageChange={handleOnPageChange}
            />
            {!!images.length && (
                <ModalCarousel
                    initialSlide={initialSlide}
                    images={images || []}
                    items={reviews || []}
                    onClose={() => setOpen(false)}
                    open={open}
                />
            )}
        </Container>
    );
};

export default Reviews;
